import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { InfoTooltip } from '@/components/InfoTooltip';
import styles from '../DashboardOverviewNew.module.scss';
import { PRODUCTIVITY_CHART_TICKS } from '../data/dashboardOverviewNew.data';

const PRODUCTIVITY_OVER_TIME_TOOLTIP = {
  description:
    'Plots the productivity score hour by hour through the day, alongside the department average for the same hours — useful for spotting when focus dips or peaks.',
  calculation: {
    formula:
      'Hourly score = (Productive seconds in hour / Total tracked seconds in hour) × 100',
  },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid var(--gray-200)',
        borderRadius: 8,
        padding: '8px 12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        fontSize: 12,
      }}
    >
      <div style={{ fontWeight: 600, color: '#101828', marginBottom: 4 }}>
        {payload[0]?.value}%
      </div>
      <div style={{ color: 'var(--gray-500)' }}>{label}</div>
    </div>
  );
};

export const ProductivityOverTime = ({
  data,
  isLoading = false,
  chartTicks,
}) => (
  <div
    className={styles.chartCardWide}
    style={isLoading ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
  >
    <div className={styles.cardHeader}>
      <div className={styles.cardTitleGroup}>
        <span className={styles.cardTitle}>Productivity Over Time</span>
        <InfoTooltip
          title="Productivity Over Time"
          {...PRODUCTIVITY_OVER_TIME_TOOLTIP}
        />
      </div>
    </div>

    <div className={styles.chartLegend}>
      <div className={styles.legendItem}>
        <div
          className={styles.legendLineSolid}
          style={{ background: '#4F7EF8', position: 'relative' }}
        >
          <span
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%,-50%)',
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#4F7EF8',
              display: 'block',
            }}
          />
        </div>
        Productivity Score (%)
      </div>
      <div className={styles.legendItem}>
        <div
          className={styles.legendLineSolid}
          style={{
            background: 'transparent',
            borderTop: '2px dashed #a78bfa',
            width: 20,
          }}
        />
        Department Average (%)
      </div>
    </div>

    <ResponsiveContainer width="100%" height={220}>
      <LineChart
        data={data ?? []}
        margin={{ top: 8, right: 8, left: -20, bottom: 0 }}
      >
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="var(--gray-100)"
          vertical={false}
        />
        <XAxis
          dataKey="time"
          ticks={chartTicks ?? PRODUCTIVITY_CHART_TICKS}
          tick={{ fontSize: 11, fill: 'var(--gray-400)' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          domain={[0, 100]}
          ticks={[0, 25, 50, 75, 100]}
          tickFormatter={(v) => `${v}%`}
          tick={{ fontSize: 11, fill: 'var(--gray-400)' }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={<CustomTooltip />} />
        <Line
          type="monotone"
          dataKey="score"
          stroke="#4F7EF8"
          strokeWidth={2}
          dot={{ fill: '#4F7EF8', r: 3, strokeWidth: 0 }}
          activeDot={{ r: 6, fill: '#4F7EF8' }}
        />
        <Line
          type="monotone"
          dataKey="teamAvg"
          stroke="#a78bfa"
          strokeWidth={1.5}
          strokeDasharray="5 4"
          dot={false}
          activeDot={{ r: 4, fill: '#a78bfa' }}
        />
      </LineChart>
    </ResponsiveContainer>
  </div>
);
